import { useEffect } from "react";

const LOADING_CONTAINER_ID = "loading-container";
const DEFAULT_ASSET_WAIT_MS = 20000;

export const showLoadingScreen = () => {
  document
    .getElementById(LOADING_CONTAINER_ID)
    ?.classList.remove("hide-loading-container");
};

export const hideLoadingScreen = () => {
  document
    .getElementById(LOADING_CONTAINER_ID)
    ?.classList.add("hide-loading-container");
};

const waitForImage = (image) =>
  new Promise((resolve) => {
    if (image.complete) {
      resolve();
      return;
    }

    const finish = () => {
      image.removeEventListener("load", finish);
      image.removeEventListener("error", finish);
      resolve();
    };
    image.addEventListener("load", finish, { once: true });
    image.addEventListener("error", finish, { once: true });
  });

export const waitForImages = async (
  images,
  maxWaitMs = DEFAULT_ASSET_WAIT_MS,
) => {
  const pendingImages = Array.from(images || []).filter(
    (image) => image?.src && !image.complete,
  );
  if (pendingImages.length === 0) return;

  let timeoutId;
  const timeout = new Promise((resolve) => {
    timeoutId = window.setTimeout(resolve, maxWaitMs);
  });

  try {
    await Promise.race([Promise.all(pendingImages.map(waitForImage)), timeout]);
  } finally {
    window.clearTimeout(timeoutId);
  }
};

const waitForPaint = () =>
  new Promise((resolve) => {
    const nextFrame =
      window.requestAnimationFrame ||
      ((callback) => window.setTimeout(callback, 0));
    nextFrame(() => nextFrame(resolve));
  });

export const waitForPageRender = async () => {
  const root = document.getElementById("root") || document.body;
  await waitForImages(root.querySelectorAll("img"));
  await waitForPaint();
};

/**
 * Keeps the pre-React splash visible until page data has rendered and the
 * page's initial images have either loaded or failed.
 */
const usePageLoadingScreen = (isReady, resetKey) => {
  useEffect(() => {
    showLoadingScreen();
  }, [resetKey]);

  useEffect(() => {
    if (!isReady) return undefined;

    let cancelled = false;
    waitForPageRender()
      .catch((error) => {
        console.error("Unable to wait for the page render.", error);
      })
      .then(() => {
        if (!cancelled) hideLoadingScreen();
      });

    return () => {
      cancelled = true;
    };
  }, [isReady, resetKey]);
};

export default usePageLoadingScreen;
